/**
 * Accordion Component V2
 *
 * Collapsible content sections (ersetzt Bootstrap Accordion)
 * WCAG 2.1 Level AA + BFSG konform
 * Built with Radix UI Accordion for accessibility
 */

import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// Accordion Root
// ============================================================================

const Accordion = AccordionPrimitive.Root;

// ============================================================================
// Accordion Item
// ============================================================================

const AccordionItem = forwardRef<
  ElementRef<typeof AccordionPrimitive.Item>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      'border-b border-neutral-200',
      className
    )}
    {...props}
  />
));

AccordionItem.displayName = 'AccordionItem';

// ============================================================================
// Accordion Trigger
// ============================================================================

const AccordionTrigger = forwardRef<
  ElementRef<typeof AccordionPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'flex flex-1 items-center justify-between py-4 px-1',
        'text-sm font-medium',
        'text-neutral-900',
        'transition-all',
        'hover:underline',
        // Focus styles for accessibility
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        // BFSG: Touch target
        'min-h-[44px]',
        // Chevron rotation
        '[&[data-state=open]>svg]:rotate-180',
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className={cn(
          'h-4 w-4 shrink-0',
          'text-neutral-500',
          'transition-transform duration-200'
        )}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

// ============================================================================
// Accordion Content
// ============================================================================

const AccordionContent = forwardRef<
  ElementRef<typeof AccordionPrimitive.Content>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'overflow-hidden',
      'text-sm',
      'text-neutral-700',
      // Animation
      'data-[state=closed]:animate-accordion-up',
      'data-[state=open]:animate-accordion-down'
    )}
    {...props}
  >
    <div className={cn('pb-4 pt-0 px-1', className)}>
      {children}
    </div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// ============================================================================
// Exports
// ============================================================================

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
};

/**
 * Accordion Component Usage
 *
 * @example Single Item (collapsible)
 * ```tsx
 * <Accordion type="single" collapsible>
 *   <AccordionItem value="item-1">
 *     <AccordionTrigger>Abgeschlossene Zeitpläne</AccordionTrigger>
 *     <AccordionContent>
 *       <div className="space-y-2">
 *         {pastSchedules.map(s => <ScheduleCard key={s.id} {...s} />)}
 *       </div>
 *     </AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 * ```
 *
 * @example Multiple Items
 * ```tsx
 * <Accordion type="multiple" defaultValue={["item-1"]}>
 *   <AccordionItem value="item-1">
 *     <AccordionTrigger>Details</AccordionTrigger>
 *     <AccordionContent>Details content...</AccordionContent>
 *   </AccordionItem>
 *   <AccordionItem value="item-2">
 *     <AccordionTrigger>Einstellungen</AccordionTrigger>
 *     <AccordionContent>Settings content...</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 * ```
 */
